import { Component, EventEmitter, HostListener, Input, Output, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// Typeahead search combobox with substring-match highlighting.
// Replaces the plain <select> when the option list is too long to
// scroll (e.g. Mongo db names like MTA_LIVE-2026-05-26 through
// -2026-07-16). Case-insensitive substring match; matched chars
// render in a highlight color so the operator can visually confirm
// they're on the right row. Keyboard nav: ↑/↓ move activeIndex,
// Enter selects, Esc closes.
interface Segment { text: string; match: boolean; }

@Component({
  standalone: true,
  selector: 'app-search-combobox',
  imports: [CommonModule, FormsModule],
  templateUrl: './search-combobox.component.html',
})
export class SearchComboboxComponent implements OnChanges {
  @Input() items: string[] = [];
  @Input() value = '';
  @Input() placeholder = 'Search…';
  @Input() disabled = false;
  @Input() minWidth = '220px';
  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('inputEl') inputEl?: ElementRef<HTMLInputElement>;

  open = false;
  filterText = '';
  activeIndex = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      // When the parent flips value externally (e.g. stack change
      // reset), sync the visible text but don't fire another event.
      this.filterText = this.value || '';
    }
  }

  onFocus() {
    if (this.disabled) return;
    this.open = true;
    this.activeIndex = 0;
  }

  onInput(v: string) {
    this.filterText = v;
    this.open = true;
    this.activeIndex = 0;
  }

  @HostListener('document:keydown.escape')
  onEsc() { this.open = false; }

  onKey(ev: KeyboardEvent) {
    const list = this.filteredItems;
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      this.open = true;
      if (list.length > 0) this.activeIndex = Math.min(this.activeIndex + 1, list.length - 1);
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      this.open = true;
      if (list.length > 0) this.activeIndex = Math.max(this.activeIndex - 1, 0);
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      if (list.length > 0) this.select(list[this.activeIndex]);
    } else if (ev.key === 'Escape') {
      this.open = false;
    }
  }

  select(item: string) {
    this.value = item;
    this.filterText = item;
    this.open = false;
    this.valueChange.emit(item);
    // Return focus to the input so the operator can immediately keep
    // typing to switch selection without a mouse round-trip.
    setTimeout(() => this.inputEl?.nativeElement?.blur(), 0);
  }

  clear() {
    this.value = '';
    this.filterText = '';
    this.open = false;
    this.valueChange.emit('');
  }

  /**
   * Programmatically focus the input. Used by parents chaining combobox
   * selections — e.g. MongoExplorer punts focus from the DB combobox
   * into the Collection combobox after the DB's collection list loads.
   * (IT-F2-96)
   */
  focus(): void {
    if (this.disabled) return;
    const el = this.inputEl?.nativeElement;
    if (!el) return;
    // setTimeout so the focus lands AFTER Angular's current CD tick —
    // avoids racing an in-flight blur() from a select() call the parent
    // may have just triggered.
    setTimeout(() => {
      el.focus();
      // Position the caret at the end so any existing filterText is
      // visible + immediately extendable, not select-all-replaced.
      const len = el.value?.length ?? 0;
      try { el.setSelectionRange(len, len); } catch { /* not text-like */ }
    }, 0);
  }

  get filteredItems(): string[] {
    const q = (this.filterText || '').trim().toLowerCase();
    if (!q) return this.items;
    return (this.items || []).filter((it) => (it || '').toLowerCase().includes(q));
  }

  /**
   * Split an item into segments where matching substrings are flagged
   * so the template can render them in the highlight color. Case-
   * insensitive match on the current filterText; empty filter → single
   * unmatched segment.
   */
  segmentsFor(item: string): Segment[] {
    const q = (this.filterText || '').trim();
    if (!q) return [{ text: item, match: false }];
    const lc = item.toLowerCase();
    const lcQ = q.toLowerCase();
    const out: Segment[] = [];
    let i = 0;
    while (i < item.length) {
      const idx = lc.indexOf(lcQ, i);
      if (idx === -1) {
        out.push({ text: item.slice(i), match: false });
        break;
      }
      if (idx > i) out.push({ text: item.slice(i, idx), match: false });
      out.push({ text: item.slice(idx, idx + q.length), match: true });
      i = idx + q.length;
    }
    return out;
  }

  trackByIndex(i: number, _s: Segment) { return i; }
  trackByItem(_i: number, s: string) { return s; }
}
